import { cloneDeep } from 'lodash';

/**
 * Campaign ideas: the saved templates an admin publishes for their account to start from.
 *
 * Despite the name and namespace ('snailblast/campaign'), this no longer has anything to do with
 * running a campaign. Everything that did - the EDDM route picker, the mailing-list upload and
 * mapping, the list-purchase audience builder, and the campaign save/apply/refresh cycle - moved to
 * the CheckoutApp and its stores. What is left is idea authoring and browsing, which is a dashboard
 * concern and stays here.
 *
 * `list_purchases` survives that split because an idea carries a filter template: it holds exactly
 * one record while the filter editor is open. The editing itself happens in the CheckoutApp embed;
 * this store only loads the record and saves what comes back. See campaign/idea/Edit.vue.
 */

const state = {
  list_purchases: [],
  ideas: [],
  idea_projects: [],
  possible_idea_projects: []
};

const getters = {
  list_purchases: (state) => state.list_purchases || [],
  ideas(state) {
    return state.ideas || [];
  },
  idea_projects(state) {
    return state.idea_projects || [];
  },
  possible_idea_projects(state) {
    return state.possible_idea_projects || [];
  }
};

const mutations = {
  SET_LIST_PURCHASE(state, items) {
    state.list_purchases = cloneDeep(items || []);
  },
  SET_IDEAS(state, list) {
    state.ideas = list;
  },
  SET_IDEA_PROJECTS(state, list) {
    state.idea_projects = list;
  },
  DELETE_IDEA(state, id) {
    const index = state.ideas.findIndex((x) => x.id === id);
    if (index >= 0) {
      state.ideas.splice(index, 1);
    }
  },
  SET_POSSIBLE_IDEA_PROJECTS(state, list) {
    state.possible_idea_projects = list;
  }
};

const actions = {
  async getIdeas({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get('snailblast/getIdeas')
        .then((response) => {
          commit('SET_IDEAS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getIdeaUrl({ commit }, request) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get(`snailblast/getIdeaUrl?id=${request.id}&projectId=${request.projectId}`)
        .then((response) => {
          resolve(response.data.url);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getIdeaProjects({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get(`snailblast/getIdeaProjects?id=${id}`)
        .then((response) => {
          commit('SET_IDEA_PROJECTS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getPossibleIdeaProjects({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get('snailblast/getPossibleIdeaProjects')
        .then((response) => {
          commit('SET_POSSIBLE_IDEA_PROJECTS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async saveIdea({ commit }, idea) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post('snailblast/saveIdea', idea)
        .then((response) => {
          commit('SET_IDEAS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getIdeaFilters({ commit }, id) {
    return new Promise((resolve, reject) => {
      if (!id) {
        commit('SET_LIST_PURCHASE', []);
        resolve();
      } else {
        this._vm.$api
          .get(`snailblast/getIdeaFilters?id=${id}`)
          .then((response) => {
            commit('SET_LIST_PURCHASE', [response.data]);
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      }
    });
  },
  async saveIdeaFilters({ commit }, listPurchase) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post('snailblast/saveIdeaFilters', listPurchase)
        .then((response) => {
          commit('SET_LIST_PURCHASE', [response.data]);
          resolve(response.data.id);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async deleteIdea({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .delete(`snailblast/deleteIdea?id=${id}`)
        .then((response) => {
          commit('DELETE_IDEA', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
